import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  Typography,
} from '@mui/material';
import { MapIcon, ModuleConfig } from '@schamburg-web/interfaces';
import React, { useEffect, useRef, useState } from 'react';
import { ModuleCardProps } from '../../modules/types';
import {
  getAgencyName,
  getEventSubtype,
  getIncidentIcon,
  getLocationDescription,
  getNormalizedIcon,
  getRoadworkIcon,
  impactMapping,
  isRoadwork,
} from '../../modules/utils';
import EstimatedSchedule from '../EstimatedSchedule/EstimatedSchedule';
import PassageImage from '../PassageImage/PassageImage';
import { WazeAttribution } from '@schamburg-web/components';
import WisDotAttribution from '../WisDotAttribution';
import UpdatedTimeStamp from '../UpdatedTimeStamp/UpdatedTimeStamp';
import WebsiteButton from '../WebsiteButton/WebsiteButton';
import CardCloseButton from '../CardCloseButton/CardCloseButton';

const EventCard: React.FC<ModuleCardProps> = ({ icon, module, children }) => {
  const normalizedIcon = getNormalizedIcon(icon);
  const { wazeEvent, wisDotEvent, scheduled } = normalizedIcon || {};
  const cardRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);

  // Expand by default only when rendered outside the sidebar
  useEffect(() => {
    if (cardRef.current) {
      const inSidebar = cardRef.current.closest('.sidebar-type-container');
      if (!inSidebar) {
        setExpanded(true);
      }
    }
  }, []);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Card className="card-body" ref={cardRef}>
      <EventHeader
        icon={normalizedIcon}
        module={module}
        onExpandClick={handleExpandClick}
      />

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box>
          <Divider sx={{ width: '90%', marginLeft: '5%' }} />
        </Box>
        <CardContent
          sx={{
            pt: 2,
            mb: 0,
            pb: 0,
            '&:last-child': { pb: 1 },
          }}
        >
          <Box sx={{ p: 0, m: 0 }}>{children}</Box>

          <EstimatedSchedule
            scheduled={scheduled}
            schedules={normalizedIcon.schedules}
          />
          <WebsiteButton normalizedIcon={normalizedIcon} />
          {normalizedIcon.fileId && (
            <Box className={'event-image'}>
              <PassageImage
                imageUrl={'rest/file/'}
                filename={normalizedIcon.fileId}
                description={`${getLocationDescription(
                  normalizedIcon
                )} - Event Image`}
              />
            </Box>
          )}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
          >
            {wazeEvent && <WazeAttribution />}
            {wisDotEvent && <WisDotAttribution />}
            {!wazeEvent && !wisDotEvent && (
              <Typography variant="caption" color="text.secondary">
                {getAgencyName(normalizedIcon)}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
};

const EventHeader: React.FC<{
  icon: MapIcon;
  module: ModuleConfig;
  onExpandClick: () => void;
}> = ({ icon, module, onExpandClick }) => {
  const normalizedIcon = getNormalizedIcon(icon);

  return (
    <CardHeader
      action={<CardCloseButton />}
      data-card-header="true"
      onClick={onExpandClick}
      sx={{
        p: 1,
        paddingLeft: 2,
        paddingRight: 2,
        paddingTop: 1,
        paddingBottom: 1,
        minHeight: 0,
        cursor: 'pointer',
      }}
      avatar={
        <>
          {isRoadwork(normalizedIcon) && (
            <Avatar
              {...getRoadworkIcon(normalizedIcon)}
              sx={{ width: 40, height: 40 }}
            />
          )}
          {!isRoadwork(normalizedIcon) && (
            <Avatar
              {...getIncidentIcon(normalizedIcon)}
              sx={{ width: 40, height: 40 }}
            />
          )}
        </>
      }
      title={
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">{`${getEventSubtype(
              normalizedIcon
            )}`}</Typography>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gap={1}
          >
            <UpdatedTimeStamp
              icon={normalizedIcon}
              sx={{
                marginTop: -1,
                display: 'inline-flex',
                marginLeft: 3,
                marginRight: 1,
                textAlign: 'right',
              }}
            />
          </Box>
        </Box>
      }
      subheader={
        <Box>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontWeight: 'bold' }}
          >
            {normalizedIcon && normalizedIcon.location
              ? `${getLocationDescription(normalizedIcon)}${
                  normalizedIcon.scheduled ? ' (Planned)' : ''
                }`
              : 'No location provided'}
          </Typography>
          <Typography variant="body2" className="event-impact">
            {impactMapping[normalizedIcon.impact]
              ? `Traffic Impact: ${impactMapping[normalizedIcon.impact]}`
              : 'N/A'}
          </Typography>
        </Box>
      }
    />
  );
};

export default EventCard;
